import moment from "moment-timezone";

import { useEffect, useRef, useState } from "react";
import { ItemsService, SalesService } from "shared/services";

import './CountdownHoc.scss';

export const COUNTDOWN_TYPE = {
    SALE: 'sale',
    ITEM: 'item'
};

const SaleCountDownLabelMapping = {
    STARTS_CLOSING_IN: 'Starts closing in',
    LAST_ITEM_CLOSES_IN: 'Last item closes in',
    CLOSED: 'Sale Closed'
};

const ItemCountDownLabelMapping = {
    LESS_THAN_A_MINUTE: 'Less than a minute to bid',
    SOFT_CLOSE: 'Soft close in progress',
    CLOSED: 'Item Closed'
};

export const CountdownHoc = ({ targetTime, type, saleId, itemId, displayAsRow=false, showFullLabel=false, onDoneCallback, onMinCallback }) => {

    const interval = useRef(null);
    const targetUtc = useRef(null);

    const [timer, setTimer] = useState({ loaded: false, days: '', hours: '', minutes: '', seconds: '', minsAvailable: '' });
    const [checkingTime, setCheckingTime] = useState(false);
    const [countDownLabel, setCountDownLabel] = useState('');
    const [countDownOver, setCountDownOver] = useState(false);

    useEffect(() => {    
        
        clearCountdown();

        if ( targetTime ) {
            interval.current = setInterval(runTimer, 1000);
            targetUtc.current = targetTime;

            runTimer();

            if ( type === COUNTDOWN_TYPE.SALE ) {
                setCountDownLabel(SaleCountDownLabelMapping.STARTS_CLOSING_IN);
            } else {
                setCountDownLabel('');
            }
        }

        return clearCountdown;
    }, [targetTime]);

    useEffect(() => {
        if ( countDownOver ) {
            if ( type === COUNTDOWN_TYPE.SALE ) {
                runSaleTimerLogic();
            } else {
                runItemTimerLogic();
            }
        }
    }, [countDownOver]);

    const runTimer = () => {
        const currentTime = moment.utc();
        const target = moment.utc(targetUtc.current);

        const timeBetween = moment.duration(target.diff(currentTime));

        let days = timeBetween.asDays();
        days = days.toString().split('.')[0];

        let hours = timeBetween.hours();
        let minutes = timeBetween.minutes();
        let seconds = timeBetween.seconds();

        const minsAvailable = timeBetween.asMinutes();

        // Countdown time is over now
        if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {

            // Countdown time is over now for item
            if ( type === COUNTDOWN_TYPE.ITEM ) {

                // If it is item based clock. We will display soft closing message for 10 seconds.
                // NOTE: Soft close will only display if the days is 0, hours is 0, minutes == 0, seconds between 0 to -10.
                if ( days == 0 && hours == 0 && minutes == 0 && seconds <= 0 && seconds >= -10 ) {
                    setCountDownLabel(ItemCountDownLabelMapping.SOFT_CLOSE);
                } else {
                    setCountDownOver(true);
                }

            } else {
                setCountDownOver(true);
            }
        } else if ( type === COUNTDOWN_TYPE.ITEM && minsAvailable < 1 ) { // We displaying less than a minute
            setCountDownLabel(ItemCountDownLabelMapping.LESS_THAN_A_MINUTE);
        }

        if (days < 10) {
            days = "0" + days;
        }

        if (hours < 10) {
            hours = "0" + hours;
        }

        if (minutes < 10) {
            minutes = "0" + minutes;
        }

        if (seconds < 10) {
            seconds = "0" + seconds;
        }

        setTimer({ loaded: true, days, hours, minutes, seconds, minsAvailable });
        onMinCallback && onMinCallback(minsAvailable)
    };

    const runSaleTimerLogic = () => {
        if ( saleId ) {
            setCheckingTime(true);
            SalesService.getSaleClosingTime(saleId).then(response => {
                if ( response.data.data.length > 0 ) {
                    const lastItemCloseTime = response.data.data[0]['itemclosetime'];

                    if ( moment.utc(lastItemCloseTime).isSameOrBefore(moment().utc()) ) {
                        setCountDownLabel(SaleCountDownLabelMapping.CLOSED);
                        clearCountdown();
                        onDoneCallback && onDoneCallback();
                    } else {
                        setCountDownLabel(SaleCountDownLabelMapping.LAST_ITEM_CLOSES_IN);

                        targetUtc.current = lastItemCloseTime;

                        setCountDownOver(false);
                    }

                    // Delayed by 1s in displaying the next timer.
                    setTimeout(() => {
                        setCheckingTime(false);
                    }, 1000);
                }
            }).catch((error) => {});
        }
    };

    const runItemTimerLogic = () => {
        if ( itemId ) {
            setCheckingTime(true);
            let payload = {
                "data": { "itemIds": [itemId] }
            }
            ItemsService.getCurrentBidValue(payload).then(response => {
                if ( response.data.data.length > 0 ) {
                    const itemCloseTime = response.data.data[0]['itemclosetime'];

                    if ( response.data.data[0]['bidstatus'] === 'Closed' ) {
                        setCountDownLabel(ItemCountDownLabelMapping.CLOSED);
                        clearCountdown();
                        onDoneCallback && onDoneCallback();
                    } else if ( moment.utc(response.data.data[0]['itemclosetime']).isAfter(moment().utc()) ) {

                        targetUtc.current = itemCloseTime;

                        setCountDownLabel('');
                        setCountDownOver(false);
                    }

                    // Delayed by 1s in displaying the next timer.
                    setTimeout(() => {
                        setCheckingTime(false);
                    }, 1000);
                }
            }).catch(error => {});
        }
    };

    const clearCountdown = () => {
        if ( interval.current ) {
            clearInterval(interval.current);
        }
    };

    return (
        <div className={`countdown ${displayAsRow ? 'display-as-row' : ''}`}>
            {
                checkingTime ? 
                <div className="text-danger">Checking Closing Time</div> : 
                <>
                    {
                        timer.loaded ? 
                        <>
                            {
                                countDownLabel ? <div className={countDownLabel === SaleCountDownLabelMapping.CLOSED || Object.values(ItemCountDownLabelMapping).includes(countDownLabel) ? 'text-danger' : ''}>{countDownLabel}</div> : null
                            }

                            {
                                type === COUNTDOWN_TYPE.SALE ? 
                                <>
                                    {
                                        countDownLabel !== SaleCountDownLabelMapping.CLOSED ? 
                                        <div className={`sale-timer ${countDownLabel === SaleCountDownLabelMapping.LAST_ITEM_CLOSES_IN ? 'bg-red' : ''}`}>
                                            <div className="timing">
                                                <div className="num">{timer.days}</div>
                                                <div>Days</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.hours}</div>
                                                <div>Hrs</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.minutes}</div>
                                                <div>Mins</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.seconds}</div>
                                                <div>Secs</div>
                                            </div>
                                        </div> : null
                                    }
                                </> : null
                            }

                            {
                                type === COUNTDOWN_TYPE.ITEM ? 
                                <>
                                    {
                                        countDownLabel === '' ? 
                                        <div className={`item-timer ${showFullLabel ? 'detail-view' : ''} ${timer.minsAvailable < 5 ? 'bg-red' : ''}`}>
                                            <div className="timing">
                                                <div className="num">{timer.days}</div>
                                                <div>{showFullLabel ? 'Days' : 'd'}</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.hours}</div>
                                                <div>{showFullLabel ? 'Hrs' : 'h'}</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.minutes}</div>
                                                <div>{showFullLabel ? 'Mins' : 'm'}</div>
                                            </div>
                                            <div>:</div>
                                            <div className="timing">
                                                <div className="num">{timer.seconds}</div>
                                                <div>{showFullLabel ? 'Secs' : 's'}</div>
                                            </div>
                                        </div> : null
                                    }
                                </> : null
                            }
                        </> : null
                    }
                </>

            }
        </div>
    )
};